import React, {useState,useCallback,useEffect} from 'react';
import Dialog from '@material-ui/core/Dialog';
import DialogTitle from '@material-ui/core/DialogTitle';
import DialogContent from '@material-ui/core/DialogContent';
import DialogActions from '@material-ui/core/DialogActions';
import Button from '@material-ui/core/Button';
import Typography from '@material-ui/core/Typography';
import Box from '@material-ui/core/Box';
import Divider from '@material-ui/core/Divider';
import MenuItem from '@material-ui/core/MenuItem';
import Select from '@material-ui/core/Select';
import IconButton from '@material-ui/core/IconButton';
import CloseIcon from '@material-ui/icons/Close';
import CircularProgress from '@material-ui/core/CircularProgress';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import strings from 'strings';
import {centsToUSD} from "../../../tools/formatters";
import {addCredits, buyCredits} from "../../../actions/client";
import {loadStripe} from "@stripe/stripe-js";
import {TextField} from "@material-ui/core";

export default function BuyCreditsDialog({
                                             open,
                                             onClose,
                                             planName,
                                             creditCost,
                                             creditsAvailable,
                                             initialAmount,
                                             fetchSubscriptionDetails,
                                             fetchUsageData
                                         }) {
    const creditOptions = [5, 10, 20, 50, 100];
    const [selectedAmount, setSelectedAmount] = useState(creditOptions[0]);

    // Preselect the deep-link amount (?credits=<n>) when the dialog opens.
    useEffect(() => {
        if (open && initialAmount && Number(initialAmount) > 0) {
            setSelectedAmount(Number(initialAmount));
        }
    }, [open, initialAmount]);
    const [loading, setLoading] = useState(false);
    const [showSuccess, setShowSuccess] = useState(false);
    const totalPrice = centsToUSD(selectedAmount * creditCost);
    const [showFailed, setShowFailed] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');

    const purchaseCredits = (amountInCents, amountOfCredits) => {
        setLoading(true);
        const payload = {
            amountInCents: Number(amountInCents),
            amountOfCredits: Number(amountOfCredits)
        };

        return buyCredits(payload)
            .then(async res => {
                if(res.status ==="REQUIRES_ACTION"){
                    await handlePayment(res.sessionCode,payload);
                }else{
                    setShowSuccess(true);
                    fetchSubscriptionDetails();
                    fetchUsageData();
                    setLoading(false);
                }
            })
            .catch(err => {
                console.error("Error purchasing credits:", err);
                setErrorMessage('An error occurred while processing your payment. Please try again.');
                setShowFailed(true);
                setLoading(false);

            });
    };

    const handlePayment = useCallback(async (clientSecret,payload) => {
        const stripePromise = await loadStripe(process.env.REACT_APP_STRIPE_PK);
        const result = await stripePromise.confirmCardPayment(clientSecret);
        if (result.error) {
            console.error("Payment failed:", result.error.message);
            setErrorMessage(result.error.message);
            setShowFailed(true);
            setLoading(false);
        } else if (result.paymentIntent.status === "succeeded") {
            try {
                payload.paymentIntentId = result.paymentIntent.id;
                await addCredits(payload);
                setShowSuccess(true);
                fetchSubscriptionDetails();
                fetchUsageData();
                setLoading(false);
            } catch (err) {
                console.error("Error adding credits:", err);
                setErrorMessage('Payment succeeded but credits could not be added. Please contact support.');
                setShowFailed(true);
                setLoading(false);
            }
        }
    }, [fetchSubscriptionDetails,fetchUsageData]);

    const handleFailedClose = () => {
        setShowFailed(false);
        setErrorMessage('');
        onClose();
    };

    const handleSuccessClose = () => {
        setShowSuccess(false);
        onClose();
    };

    return (
        <>
            <Dialog open={open && !showSuccess} onClose={onClose} maxWidth="sm" fullWidth>
                <DialogTitle disableTypography
                             style={{display: 'flex', alignItems: 'center', justifyContent: 'space-between'}}>
                    <Typography variant="h6">{strings.BILLING.BUY_MORE_CREDITS}</Typography>
                    <IconButton aria-label="close" onClick={onClose} size="small" disabled={loading}>
                        <CloseIcon/>
                    </IconButton>
                </DialogTitle>
                <Divider/>
                <DialogContent style={{paddingBottom: 32}}>
                    <Typography variant="subtitle1" gutterBottom>
                        {strings.BILLING.BUY_CREDITS_SUBTITLE?.replace('{planName}', planName) || `Purchase additional credits for your ${planName}.`}
                    </Typography>
                    <Box
                        bgcolor="var(--bg-canvas)"
                        borderRadius={2}
                        p={2}
                        mb={2}
                        display="flex"
                        flexDirection="column"
                    >
                        <Typography variant="body1" style={{fontWeight: 600, width: "100%"}}>
                            {planName}
                        </Typography>
                        <Box display="flex" alignItems="center" justifyContent="space-between" mt={1}>
                            <Typography variant="body2" color="textSecondary">
                                {strings.BILLING.BUY_CREDITS_COST?.replace('{creditCost}', centsToUSD(creditCost) || `Credits cost ${centsToUSD(creditCost)} each`)}
                            </Typography>
                            <Typography variant="body2" color="textSecondary" style={{fontWeight: 500}}>
                                {strings.BILLING.BUY_CREDITS_USED.replace('{creditsAvailable}', creditsAvailable)}
                            </Typography>
                        </Box>
                    </Box>
                    {/* Select Credit Amount */}
                    <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                        <Typography variant="body2">{strings.BILLING.BUY_CREDITS_SELECT_AMOUNT}</Typography>
                        <Select
                            // The free TextField below is the source of truth, so the amount
                            // (e.g. a deep-link ?credits=37) may not be one of the presets.
                            // Keep the Select value within its options to avoid MUI's
                            // out-of-range warning, but still display the custom amount.
                            value={creditOptions.includes(selectedAmount) ? selectedAmount : ''}
                            displayEmpty
                            renderValue={(val) => (val === '' ? selectedAmount : val)}
                            onChange={e => setSelectedAmount(e.target.value)}
                            variant="outlined"
                            size="small"
                            style={{minWidth: 80}}
                            disabled={loading}
                        >
                            {creditOptions.map(opt => (
                                <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                            ))}
                        </Select>
                    </Box>
                    <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                        <Typography variant="body2">{strings.BILLING.BUY_CREDITS_CREDITS_LABEL}</Typography>
                        <TextField
                            type="number"
                            value={selectedAmount}
                            onChange={e => setSelectedAmount(e.target.value === '' ? '' : Number(e.target.value))}
                            variant="outlined"
                            size="small"
                            style={{ width: 80 }}
                            disabled={loading}
                            inputProps={{ min: 1, step: 1 }}
                        />
                    </Box>
                    {/* Credits row */}
                    <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                        <Typography variant="body2">{strings.BILLING.BUY_CREDITS_CREDITS_LABEL}</Typography>
                        <Typography variant="body2">{selectedAmount}</Typography>
                    </Box>
                    {/* Price per credit row */}
                    <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                        <Typography variant="body2">{strings.BILLING.BUY_CREDITS_TOTAL_PER_CREDIT}</Typography>
                        <Typography variant="body2">{centsToUSD(creditCost)}</Typography>
                    </Box>
                    <Divider style={{margin: '12px 0'}}/>
                    {/* Total price row */}
                    <Box display="flex" alignItems="center" justifyContent="space-between" mb={3}>
                        <Typography variant="subtitle2"
                                    style={{fontWeight: 600}}>{strings.BILLING.BUY_CREDITS_TOTAL_PRICE}</Typography>
                        <Typography variant="subtitle2" style={{fontWeight: 600}}>{totalPrice}</Typography>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={() => purchaseCredits(selectedAmount * creditCost, selectedAmount)}
                        color="primary"
                        variant="contained"
                        disabled={loading || !selectedAmount || selectedAmount <= 0}
                        startIcon={loading ? <CircularProgress size={20} color="inherit"/> : null}
                    >
                        {loading ? 'Processing...' : strings.BILLING.BUY_CREDITS_PURCHASE}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Success Dialog */}
            <Dialog open={showSuccess} onClose={handleSuccessClose} maxWidth="sm" fullWidth>
                <DialogContent style={{textAlign: 'center', padding: '32px'}}>
                    <CheckCircleIcon style={{fontSize: 64, color: 'var(--status-approved)', marginBottom: 16}}/>
                    <Typography variant="h6" gutterBottom>
                        Credits Purchased Successfully!
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                        {selectedAmount} credits have been added to your account.
                    </Typography>
                </DialogContent>
                <DialogActions style={{justifyContent: 'center', paddingBottom: 24}}>
                    <Button onClick={handleSuccessClose} color="primary" variant="contained">
                        Continue
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Failed Dialog */}
            <Dialog open={showFailed} onClose={handleFailedClose} maxWidth="sm" fullWidth>
                <DialogContent style={{textAlign: 'center', padding: '32px'}}>
                    <Box style={{fontSize: 64, color: 'var(--status-declined)', marginBottom: 16}}>❌</Box>
                    <Typography variant="h6" gutterBottom>
                        Payment Failed
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                        {errorMessage}
                    </Typography>
                </DialogContent>
                <DialogActions style={{justifyContent: 'center', paddingBottom: 24}}>
                    <Button onClick={handleFailedClose} color="primary" variant="contained">
                        Try Again
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
}